import React, { useEffect, useState } from "react";
import { authFetch, authFetchForm } from "../../api";

const inputClass = "w-full px-4 py-2 rounded-lg bg-white/5 border border-navy-border text-white focus:outline-none focus:border-accent-light";

// One image field: preview of the saved (or newly chosen) file, a file picker, and a "remove" toggle.
function ImageField({ label, hint, saved, file, remove, onFile, onRemove, previewClass }) {
  const preview = file ? URL.createObjectURL(file) : remove ? null : saved;
  return (
    <div>
      <p className="text-sm text-gray-300 mb-2">{label}</p>
      <div className="flex items-center gap-4 flex-wrap">
        <div className="w-28 h-28 shrink-0 rounded-lg bg-navy-dark border border-navy-border flex items-center justify-center overflow-hidden">
          {preview ? <img src={preview} alt={`${label} preview`} className={previewClass} /> : <span className="text-xs text-gray-500 text-center px-2">Using the site default</span>}
        </div>
        <div className="space-y-2">
          <input type="file" accept="image/*" onChange={(e) => onFile(e.target.files[0] || null)} className="text-sm text-gray-300" />
          {(saved || file) && !remove && (
            <button type="button" onClick={onRemove} className="block text-xs text-red-400 hover:underline">
              Remove {file ? "the chosen file" : "and use the site default"}
            </button>
          )}
          {remove && <p className="text-xs text-amber-300">Will be removed when you save.</p>}
        </div>
      </div>
      <p className="text-xs text-gray-500 mt-2">{hint}</p>
    </div>
  );
}

// The Co-Work pages' own identity: browser tab title, favicon and logo. Anything left empty falls back
// to the site-wide favicon / logo (Site Settings > Site Identity).
export default function CoWorkBrandingSettings() {
  const [data, setData] = useState(null);
  const [title, setTitle] = useState("");
  const [files, setFiles] = useState({ favicon: null, logo: null });
  const [removed, setRemoved] = useState({ favicon: false, logo: false });
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () =>
    authFetch("/core/cowork/settings/")
      .then((res) => res.json())
      .then((d) => { setData(d); setTitle(d.browser_title || ""); })
      .catch(() => setError("Failed to load."));

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMessage("");
    setError("");
    const fd = new FormData();
    fd.append("browser_title", title);
    ["favicon", "logo"].forEach((field) => {
      if (files[field]) fd.append(field, files[field]);
      else if (removed[field]) fd.append(field, "");   // an empty value clears the saved image
    });
    try {
      const res = await authFetchForm("/core/cowork/settings/", fd);
      if (!res.ok) throw new Error();
      const d = await res.json();
      setData(d);
      setFiles({ favicon: null, logo: null });
      setRemoved({ favicon: false, logo: false });
      setMessage("Saved.");
    } catch {
      setError("Failed to save.");
    }
    setBusy(false);
  };

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-bold mb-1">Browser Title &amp; Logo</h1>
      <p className="text-sm text-gray-400 mb-6">
        How the Co-Work pages (marketplace, job pages and dashboards) appear in the browser: the tab title, the tab icon
        and the logo in the header and the sign-in window.
      </p>

      {error && <p className="text-sm text-red-400 mb-4">{error}</p>}

      {!data ? (
        <p className="text-gray-400">Loading...</p>
      ) : (
        <form onSubmit={save} className="space-y-8">
          <div>
            <label className="block text-sm text-gray-300 mb-1">Browser Title</label>
            <input className={inputClass} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Co-Work Marketplace" />
            <p className="text-xs text-gray-500 mt-1">
              Shown in the browser tab on the Co-Work page. Job pages show it as "Job title - Browser Title".
            </p>
          </div>

          <ImageField
            label="Favicon"
            hint="The small icon in the browser tab. A square PNG (at least 64×64) works best."
            saved={data.favicon}
            file={files.favicon}
            remove={removed.favicon}
            onFile={(f) => { setFiles({ ...files, favicon: f }); setRemoved({ ...removed, favicon: false }); }}
            onRemove={() => { setFiles({ ...files, favicon: null }); setRemoved({ ...removed, favicon: true }); }}
            previewClass="w-16 h-16 object-contain"
          />

          <ImageField
            label="Logo"
            hint="Shown in the header and at the top of the sign-in window on the Co-Work pages. A PNG with a transparent background works best."
            saved={data.logo}
            file={files.logo}
            remove={removed.logo}
            onFile={(f) => { setFiles({ ...files, logo: f }); setRemoved({ ...removed, logo: false }); }}
            onRemove={() => { setFiles({ ...files, logo: null }); setRemoved({ ...removed, logo: true }); }}
            previewClass="max-w-full max-h-full object-contain p-2"
          />

          {message && <p className="text-sm text-green-400">{message}</p>}
          <button disabled={busy} className="px-6 py-2 rounded-lg bg-brand-gradient text-white font-semibold disabled:opacity-60">
            {busy ? "Saving..." : "Save Changes"}
          </button>
        </form>
      )}
    </div>
  );
}
